/**
 * Fleet Customer API
 *
 * Workshop-side management of fleet customers and their portal logins.
 *
 * These call the FleetUserController (login management), the fleet-customer
 * list/search endpoints, and — from the fleet customer's own portal — their
 * contact details.
 */

import axiosInstance from './axiosInstance';

// ============================================
// Fleet Customer — list & search
// ============================================

/**
 * List fleet customers for the current org, paginated + searchable.
 *
 * @param {{ page?: number, size?: number, search?: string }} params
 *   - page:   zero-based page index (default 0)
 *   - size:   page size (default 20)
 *   - search: free-text match on name, contact number OR vehicle rego (optional)
 * @returns {Promise<{ content: Array, page: number, size: number, totalElements: number, totalPages: number }>}
 *   Each row: { uuid, name, contactNumber, email, address, suburb, state, postcode,
 *               vehicleCount, vehicles: [{ uuid, make, model, year, regoNumber, ... }] }
 *
 *   The address is maintained by the fleet customer from their own portal settings
 *   page — it is read-only to the workshop, which is why there is no update call for
 *   it beside this one.
 */
export const listFleetCustomersApi = async ({ page = 0, size = 20, search = '', createdFrom, createdTo } = {}) => {
    const params = { page, size };
    if (search && search.trim()) {
        params.search = search.trim();
    } else {
        // No search: the server requires a created-date window of at most a month, so the
        // list is never unbounded. Matches the walk-in customers tab.
        if (createdFrom) params.createdFrom = createdFrom;
        if (createdTo) params.createdTo = createdTo;
    }
    const response = await axiosInstance.get('/fleet-customers', { params });
    return response.data;
};

// ============================================
// Fleet Customer — create + invite (portal login)
// ============================================

/**
 * Create a fleet customer and immediately invite them to the portal.
 *
 * NOTE: creation of the Customer record (with isFleet = true) and the portal
 * login may be one endpoint or two depending on how the backend is wired. This
 * calls a single "create + invite" endpoint; the backend creates the fleet
 * Customer, then the CLIENT login, and emails the temporary password.
 *
 * @param {{ name: string, contactNumber?: string, email: string }} payload
 * @returns {Promise<{ customerUuid: string, userUuid: string, email: string, message: string }>}
 */
export const createFleetCustomerApi = async (payload) => {
    const response = await axiosInstance.post('/fleet-customers', payload);
    return response.data;
};

/**
 * Re-send onboarding (fresh temporary password) to an existing fleet login.
 * Calls FleetUserController.resend.
 */
export const resendFleetInviteApi = async (customerUuid) => {
    const response = await axiosInstance.post(`/fleet-users/${customerUuid}/resend`);
    return response.data;
};

/**
 * Suspend a fleet customer's portal login (blocks authentication).
 * Calls FleetUserController.suspend.
 */
export const suspendFleetLoginApi = async (customerUuid) => {
    const response = await axiosInstance.post(`/fleet-users/${customerUuid}/suspend`);
    return response.data;
};

/**
 * Reactivate a suspended fleet login.
 * Calls FleetUserController.reactivate.
 */
export const reactivateFleetLoginApi = async (customerUuid) => {
    const response = await axiosInstance.post(`/fleet-users/${customerUuid}/reactivate`);
    return response.data;
};

// ============================================
// Aggregate export
// ============================================

const fleetApi = {
    listFleetCustomersApi,
    createFleetCustomerApi,
    resendFleetInviteApi,
    suspendFleetLoginApi,
    reactivateFleetLoginApi,
};

export default fleetApi;
// ============================================
// Fleet portal — the caller's OWN contact details
// ============================================

/**
 * The signed-in fleet customer's own details.
 *
 * No uuid is passed, deliberately: the server reads the customer from the JWT claim.
 * A uuid here would be the one place a fleet customer could name someone else's record.
 *
 * @returns {Promise<{ uuid, name, email, phone, address, suburb, state, postcode }>}
 *   `name` and `email` are display-only — the server refuses to write either.
 */
export const getFleetSettingsApi = async () => {
    const response = await axiosInstance.get('/fleet/settings');
    return response.data;
};

/**
 * Save the signed-in fleet customer's own phone and address.
 *
 * A null field is left alone; an empty string CLEARS it. The settings form sends
 * every field on every save, so what the form shows is what gets stored.
 *
 * @param {{ phone?: string, address?: string, suburb?: string, state?: string, postcode?: string }} payload
 */
export const updateFleetSettingsApi = async (payload) => {
    const response = await axiosInstance.put('/fleet/settings', payload);
    return response.data;
};
